/**
 * Consultant session notes — Mongo-backed (any device).
 * localStorage used only as offline draft / prefs.
 */
import { apiRequest } from "./async/apiUtils";

const PREFS_KEY = "prontopsy_consultant_prefs";
const DRAFT_PREFIX = "prontopsy_note_draft_";

export function getConsultantPrefs() {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}");
  } catch (_) {
    return {};
  }
}

export function setConsultantPrefs(partial) {
  const next = { ...getConsultantPrefs(), ...partial };
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  } catch (_) {
    /* ignore */
  }
  return next;
}

export const AUTO_ONLINE_EVENT = "prontopsy-auto-online-pref";

export function getAutoOnlineOnSignIn() {
  try {
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    if (typeof ud?.autoOnlineOnSignIn === "boolean") return ud.autoOnlineOnSignIn;
  } catch (_) {
    /* ignore */
  }
  const p = getConsultantPrefs();
  return p.autoOnlineOnSignIn !== false;
}

export function setAutoOnlineOnSignIn(enabled) {
  const on = !!enabled;
  const next = setConsultantPrefs({ autoOnlineOnSignIn: on });
  try {
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    ud.autoOnlineOnSignIn = on;
    localStorage.setItem("userDetails", JSON.stringify(ud));
  } catch (_) {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent(AUTO_ONLINE_EVENT, { detail: { enabled: on } }));
  } catch (_) {
    /* ignore */
  }
  return next;
}

/** Persist the checkbox to the account so the next login (any device) honors it. */
export async function persistAutoOnlineOnSignIn(enabled) {
  setAutoOnlineOnSignIn(enabled);
  try {
    await apiRequest("put", "profile/auto-online", {
      body: { enabled: !!enabled },
    });
    return true;
  } catch (e) {
    console.warn("persistAutoOnlineOnSignIn", e);
    return false;
  }
}

function draftKey(appointmentId) {
  return `${DRAFT_PREFIX}${appointmentId}`;
}

export function saveLocalDraft(appointmentId, text) {
  if (!appointmentId) return;
  try {
    localStorage.setItem(draftKey(appointmentId), text || "");
  } catch (_) {
    /* ignore */
  }
}

export function loadLocalDraft(appointmentId) {
  if (!appointmentId) return "";
  try {
    return localStorage.getItem(draftKey(appointmentId)) || "";
  } catch (_) {
    return "";
  }
}

/** Load note from API (DB). Falls back to local draft. */
export async function fetchSessionNote(appointmentId) {
  if (!appointmentId) return { text: "", _id: null };
  try {
    const res = await apiRequest("get", "note/session", {
      queries: { appointmentId },
    });
    const data = res?.data?.data || res?.data;
    if (res?.data?.status === 200 || res?.data?.success) {
      const text = data?.message || data?.text || "";
      return {
        text,
        _id: data?._id || null,
        clientName: data?.clientName || "",
        updatedAt: data?.updatedAt || null,
      };
    }
  } catch (e) {
    console.warn("fetchSessionNote", e);
  }
  return { text: loadLocalDraft(appointmentId), _id: null, fromDraft: true };
}

/** Upsert note in DB via existing consultant-note endpoint */
export async function saveSessionNoteApi(appointmentId, message) {
  if (!appointmentId) return { ok: false };
  saveLocalDraft(appointmentId, message);
  try {
    const res = await apiRequest("post", "note/consultant-note", {
      body: {
        appointmentId,
        message: message || "",
      },
    });
    const ok = res?.data?.status === 200 || res?.data?.success;
    return { ok, data: res?.data?.data };
  } catch (e) {
    console.warn("saveSessionNoteApi", e);
    return { ok: false, error: e };
  }
}

/** List all session notes for this consultant (from DB) */
export async function fetchSessionNotesList() {
  try {
    const res = await apiRequest("get", "note/session-list", {
      queries: { limit: 50 },
    });
    if (res?.data?.status === 200 || res?.data?.success) {
      return res?.data?.data || [];
    }
  } catch (e) {
    console.warn("fetchSessionNotesList", e);
  }
  return [];
}

/** @deprecated local-only list — prefer fetchSessionNotesList */
export function listSessionNotes() {
  return [];
}

export function deleteSessionNote() {
  /* server soft-delete via note/delete if needed later */
}
